/**
 * Abaya Size Memory
 *
 * Remembers the size a customer last successfully added to cart on an abaya product, and preselects it
 * when they open a different abaya, as long as that size is available and no manual/restored selection
 * already exists. Scoped to the main PDP only (not quick add).
 *
 * The theme's <product-rerender> element fully replaces the variant-picker DOM subtree on every option
 * change (including color-only changes), so this listens at the document level throughout instead of
 * holding onto element references that would go stale after a re-render.
 */
(function () {
  "use strict";

  var DEBUG = true;
  var LOG_PREFIX = "[abaya-size-memory]";

  function log() {
    if (!DEBUG || !window.console) {
      return;
    }
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }

  function warn() {
    if (!DEBUG || !window.console) {
      return;
    }
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.warn.apply(console, args);
  }

  var STORAGE_KEY = "aya:abaya_size_memory";

  log("script loaded");

  var contextScript = document.querySelector("script[data-abaya-size-memory-context]");
  if (!contextScript) {
    warn("aborting: no [data-abaya-size-memory-context] script found on page (feature likely disabled, product not abaya-family, or not on main PDP)");
    return;
  }

  var context;
  try {
    context = JSON.parse(contextScript.textContent);
  } catch (e) {
    warn("aborting: failed to parse context JSON", e, contextScript.textContent);
    return;
  }

  log("context parsed", context);

  if (!context || !context.formId || !context.productId) {
    warn("aborting: context missing formId or productId", context);
    return;
  }

  var evaluationId = generateId();
  var suppressManualTracking = false;
  var wasPreselected = false;
  var retained = false;
  var manualChangeCount = 0;
  var messageVisible = false;
  var preselectedValueId = null;
  var previousSizeName = null;
  var memory = readMemory();
  log("memory read from localStorage", memory);

  function generateId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "eval-" + Date.now() + "-" + Math.random().toString(36).slice(2);
  }

  function readMemory() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeMemory(size, sourceProductId) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ size: size, sourceProductId: sourceProductId }));
      log("memory written to localStorage", { size: size, sourceProductId: sourceProductId });
    } catch (e) {
      // Private/incognito mode may block storage writes; memory simply won't persist.
      warn("failed to write memory to localStorage (private/incognito mode?)", e);
    }
  }

  function trackEvent(name, properties) {
    if (window.AmplitudeAnalytics && typeof window.AmplitudeAnalytics.trackEvent === "function") {
      window.AmplitudeAnalytics.trackEvent(name, properties);
    } else {
      warn("trackEvent: window.AmplitudeAnalytics.trackEvent unavailable, event dropped", name, properties);
    }
  }

  function getPicker() {
    var pickers = document.querySelectorAll('variant-picker[context="main_product"]');
    for (var i = 0; i < pickers.length; i++) {
      if (pickers[i].getAttribute("form-id") === context.formId) {
        return pickers[i];
      }
    }
    warn("getPicker: no variant-picker[context=main_product] found with form-id", context.formId, "found form-ids:", Array.prototype.map.call(pickers, function (p) { return p.getAttribute("form-id"); }));
    return null;
  }

  function getSizeFieldset() {
    var picker = getPicker();
    if (!picker) {
      return null;
    }
    var fieldset = picker.querySelector('fieldset[data-option-role="size"]');
    if (!fieldset) {
      warn("getSizeFieldset: picker found but no fieldset[data-option-role=size] inside it (size option not detected as such, check general.label.size translation match)");
    }
    return fieldset;
  }

  function readSizeValues(fieldset) {
    var script = fieldset.querySelector("script[data-abaya-size-values]");
    if (!script) {
      return [];
    }
    try {
      return JSON.parse(script.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function currentCheckedInput(fieldset) {
    return fieldset.querySelector("input[data-option-position]:checked");
  }

  function findInputByValueId(fieldset, id) {
    var inputs = fieldset.querySelectorAll("input[data-option-position]");
    for (var i = 0; i < inputs.length; i++) {
      if (String(inputs[i].value) === String(id)) {
        return inputs[i];
      }
    }
    return null;
  }

  function sizeNameForInput(input, sizeValues) {
    if (!input) {
      return null;
    }
    for (var i = 0; i < sizeValues.length; i++) {
      if (String(sizeValues[i].id) === String(input.value)) {
        return sizeValues[i].name;
      }
    }
    return null;
  }

  function syncMessageVisibility() {
    var fieldset = getSizeFieldset();
    if (!fieldset) {
      return;
    }
    var messageEl = fieldset.querySelector("[data-abaya-size-memory-message]");
    if (messageEl) {
      messageEl.hidden = !messageVisible;
    }
  }

  function syncBadgeVisibility() {
    var fieldset = getSizeFieldset();
    if (!fieldset) {
      return;
    }

    var badges = fieldset.querySelectorAll("[data-abaya-size-preselect-badge]");
    for (var i = 0; i < badges.length; i++) {
      badges[i].hidden = true;
    }

    if (!messageVisible || preselectedValueId == null) {
      return;
    }

    var badge = fieldset.querySelector('[data-abaya-size-preselect-badge][data-option-value-id="' + preselectedValueId + '"]');
    if (badge) {
      badge.hidden = false;
      log("syncBadgeVisibility: showing preselect badge for value id", preselectedValueId);
    } else {
      warn("syncBadgeVisibility: no badge element found for preselected value id", preselectedValueId);
    }
  }

  function emitEvaluated(result, rememberedSizeAvailable, preselectionApplied) {
    trackEvent("size_preselection_evaluated", {
      evaluation_id: evaluationId,
      product_id: context.productId,
      source_product_id: memory.sourceProductId,
      remembered_size: memory.size,
      remembered_size_available: rememberedSizeAvailable,
      preselection_applied: preselectionApplied,
      evaluation_result: result,
    });
  }

  function evaluatePreselection() {
    log("evaluatePreselection: start", { productId: context.productId, formId: context.formId });

    var fieldset = getSizeFieldset();
    if (!fieldset) {
      warn("evaluatePreselection: aborting, no size fieldset found for this form-id (variant-picker not rendered yet, wrong context, or product has no size option)");
      return;
    }

    var sizeValues = readSizeValues(fieldset);
    log("evaluatePreselection: size values on page", sizeValues);
    previousSizeName = sizeNameForInput(currentCheckedInput(fieldset), sizeValues);
    log("evaluatePreselection: currently selected size", previousSizeName);

    if (!memory) {
      log("evaluatePreselection: no remembered size in localStorage, nothing to do");
      return;
    }

    var isSameProduct = String(memory.sourceProductId) === String(context.productId);
    if (isSameProduct) {
      log("evaluatePreselection: skipping, memory was written from this same product", memory);
      emitEvaluated("same_product", null, false);
      return;
    }

    if (!context.productAvailable) {
      log("evaluatePreselection: skipping, context.productAvailable is falsy", context.productAvailable);
      emitEvaluated("product_unavailable", false, false);
      return;
    }

    var match = null;
    for (var i = 0; i < sizeValues.length; i++) {
      if (sizeValues[i].name === memory.size) {
        match = sizeValues[i];
        break;
      }
    }

    if (!match || !match.available) {
      warn("evaluatePreselection: remembered size not applicable", { rememberedSize: memory.size, match: match, availableSizes: sizeValues });
      emitEvaluated("size_unavailable", !!(match && match.available), false);
      return;
    }

    var currentInput = currentCheckedInput(fieldset);
    var alreadySelected = currentInput && String(currentInput.value) === String(match.id);
    log("evaluatePreselection: match found", match, "alreadySelected:", alreadySelected);

    // Only bail on an existing ?variant= param if it resolved to a size other than our remembered
    // one - otherwise this is just our own preselection surviving a refresh (we update the URL via
    // history.replaceState once applied), and we should still re-apply the highlight below.
    var hasExistingSelection = new URLSearchParams(window.location.search).has("variant");
    if (hasExistingSelection && !alreadySelected) {
      log("evaluatePreselection: skipping, URL already has a variant param for a different size (restored/direct-link selection)");
      emitEvaluated("existing_selection", true, false);
      return;
    }

    if (!alreadySelected) {
      var targetInput = findInputByValueId(fieldset, match.id);
      if (!targetInput) {
        warn("evaluatePreselection: could not find input for matched size id, skipping", match);
      } else {
        log("evaluatePreselection: checking input and dispatching change event", targetInput);
        suppressManualTracking = true;
        targetInput.checked = true;
        targetInput.dispatchEvent(new Event("change", { bubbles: true }));
        suppressManualTracking = false;
      }
    }

    wasPreselected = true;
    retained = true;
    previousSizeName = match.name;
    messageVisible = true;
    preselectedValueId = match.id;
    syncMessageVisibility();
    syncBadgeVisibility();

    log("evaluatePreselection: applied preselection for size", match.name);
    emitEvaluated("applied", true, true);
  }

  document.addEventListener("change", function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches("input[data-option-position]")) {
      return;
    }

    var fieldset = input.closest('fieldset[data-option-role="size"]');
    if (!fieldset) {
      return;
    }

    var picker = fieldset.closest('variant-picker[context="main_product"]');
    if (!picker || picker.getAttribute("form-id") !== context.formId) {
      return;
    }

    if (suppressManualTracking) {
      return;
    }

    var sizeValues = readSizeValues(fieldset);
    var newSizeName = sizeNameForInput(input, sizeValues);

    manualChangeCount += 1;
    var selectionContext;
    if (manualChangeCount === 1) {
      selectionContext = wasPreselected ? "changed_preselection" : "no_preselection";
    } else {
      selectionContext = "changed_manual_selection";
    }

    trackEvent("pdp_size_selected", {
      evaluation_id: evaluationId,
      product_id: context.productId,
      previous_size: previousSizeName,
      selected_size: newSizeName,
      selection_context: selectionContext,
    });

    log("manual size change detected", { previousSizeName: previousSizeName, newSizeName: newSizeName, selectionContext: selectionContext });

    retained = false;
    messageVisible = false;
    previousSizeName = newSizeName;
    syncBadgeVisibility();
  });

  document.addEventListener("variant:change", function (event) {
    // Not event.detail.formId: the theme dispatches this with formId set to _form.id, which is
    // shadowed by the same hidden "id" input described in formIdAttr() below, so it's never a
    // string. Key off event.target (the form itself, since the theme dispatches this event on it)
    // instead, reading the id attribute directly.
    var form = event.target;
    if (!form || formIdAttr(form) !== context.formId) {
      return;
    }
    log("variant:change event received for this form, re-syncing message and badge visibility");
    syncMessageVisibility();
    syncBadgeVisibility();
  });

  function formIdAttr(form) {
    // Not form.id: these product forms contain a hidden input named "id" (the variant id field),
    // and named form controls shadow the HTMLFormElement's own .id property in browsers, so
    // form.id resolves to that <input> element rather than the id attribute string.
    return form ? form.getAttribute("id") : null;
  }

  document.addEventListener("click", function (event) {
    var button = event.target && event.target.closest ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
    if (!button) {
      return;
    }
    var form = button.form || button.closest("form");
    log("add-to-cart button clicked", { buttonFormId: formIdAttr(form), expectedFormId: context.formId, matches: formIdAttr(form) === context.formId });
  }, true);

  document.addEventListener("submit", function (event) {
    var form = event.target;
    log("submit event captured on document", { submittedFormId: formIdAttr(form), expectedFormId: context.formId, matches: formIdAttr(form) === context.formId });
    if (!form || formIdAttr(form) !== context.formId) {
      return;
    }

    log("submit event received for this form, recording current size selection");

    var fieldset = getSizeFieldset();
    if (!fieldset) {
      warn("submit: no size fieldset found, skipping memory write");
      return;
    }

    var sizeValues = readSizeValues(fieldset);
    var selectedSizeName = sizeNameForInput(currentCheckedInput(fieldset), sizeValues);

    if (!selectedSizeName) {
      warn("submit: could not determine selected size name from current selection, skipping memory write");
      return;
    }

    writeMemory(selectedSizeName, context.productId);

    var sizeSelectionMethod = "manual";
    if (wasPreselected) {
      sizeSelectionMethod = retained ? "preselection_retained" : "preselection_changed";
    }

    log("submit: recorded add to cart", { selectedSizeName: selectedSizeName, sizeSelectionMethod: sizeSelectionMethod });

    trackEvent("Web: Add To Cart", {
      product_id: context.productId,
      selected_size: selectedSizeName,
      size_preselection_evaluation_id: evaluationId,
      size_selection_method: sizeSelectionMethod,
    });
  });

  evaluatePreselection();
})();
